import { useTranslation } from 'react-i18next';
import { formatCurrencyWhole } from '../../../utils/formatCurrency.js';
import CollapsiblePanel from './CollapsiblePanel.js';

const VISIBLE_ROWS = 7;
const ROW_HEIGHT_PX = 40;

const COLUMNS = [
  {
    key: 'undispatched',
    titleKey: 'dashboard.undispatched',
    locationKey: 'dashboard.origin',
    locationDefault: 'Origin',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
  {
    key: 'in_transit',
    titleKey: 'dashboard.inTransit',
    locationKey: 'dashboard.destination',
    locationDefault: 'Destination',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
  {
    key: 'pending_delivery',
    titleKey: 'dashboard.pendingDelivery',
    locationKey: 'dashboard.destination',
    locationDefault: 'Destination',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
  {
    key: 'pending_dispatch',
    titleKey: 'dashboard.pendingDispatch',
    locationKey: 'dashboard.origin',
    locationDefault: 'Origin',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
  {
    key: 'cargo_missing',
    titleKey: 'dashboard.cargoMissing',
    locationKey: 'dashboard.destination',
    locationDefault: 'Destination',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
];

const CargoColumn = ({ column, data, onOpen }) => {
  const { t } = useTranslation();
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const isMissing = column.key === 'cargo_missing';
  const shipmentCount = isMissing
    ? Number(totals.transit || 0) + Number(totals.delivery || 0)
    : Number(totals.gc_count || 0);
  const footerCells = [
    `Shipment : ${shipmentCount}`,
    `${t('dashboard.qty')} : ${totals.qty || 0}`,
    formatCurrencyWhole(totals.amount),
  ];

  return (
    <div className="w-full border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => onOpen(column.key, null)}
        className={`w-full px-3 py-2.5 text-center text-base font-semibold ${column.headerClass}`}
      >
        {t(column.titleKey)}
      </button>
      <div className="overflow-y-auto" style={{ maxHeight: (VISIBLE_ROWS + 1) * ROW_HEIGHT_PX }}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white text-gray-700">
            <tr>
              <th className="text-left font-semibold px-3 py-2.5">{t(column.locationKey, { defaultValue: column.locationDefault })}</th>
              {isMissing ? (
                <>
                  <th className="text-center font-semibold px-2 py-2.5">{t('dashboard.transit')}</th>
                  <th className="text-center font-semibold px-2 py-2.5">{t('dashboard.delivery')}</th>
                </>
              ) : (
                <th className="text-center font-semibold px-2 py-2.5">Shipment</th>
              )}
              <th className="text-center font-semibold px-2 py-2.5">{t('dashboard.qty')}</th>
              <th className="text-center font-semibold px-2 py-2.5">{t('dashboard.totalAmount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${column.key}-${row.hub_id || 'none'}`} className="border-t border-gray-100">
                <td className="px-3 py-2.5 text-left">
                  <button
                    type="button"
                    onClick={() => onOpen(column.key, row.hub_id)}
                    className="text-blue-600 hover:underline text-left break-words leading-snug"
                  >
                    {row.hub_name}
                    {row.is_hub ? (
                      <span className="hidden sm:inline ml-1 text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-500">Hub</span>
                    ) : null}
                  </button>
                </td>
                {isMissing ? (
                  <>
                    <td className="text-center px-2 py-2.5 tabular-nums">{row.transit || 0}</td>
                    <td className="text-center px-2 py-2.5 tabular-nums">{row.delivery || 0}</td>
                  </>
                ) : (
                  <td className="text-center px-2 py-2.5 tabular-nums">{row.gc_count || 0}</td>
                )}
                <td className="text-center px-2 py-2.5 tabular-nums">{row.qty || 0}</td>
                <td className="text-center px-2 py-2.5 tabular-nums">{formatCurrencyWhole(row.amount)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={isMissing ? 5 : 4} className="px-3 py-6 text-center text-sm text-gray-400">
                  {t('common.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-gray-100 bg-white px-3 py-2.5 text-sm text-gray-600">
        {footerCells.map((cell) => (
          <span key={`${column.key}-${cell}`} className="font-medium tabular-nums">
            {cell}
          </span>
        ))}
      </div>
    </div>
  );
};

const CargoOverviewSection = ({
  data,
  onOpenBucket,
  hubFilter = null,
}) => {
  const { t } = useTranslation();
  const asOn = data?.as_on;

  return (
    <CollapsiblePanel
      title={t('dashboard.overviewCargoCount')}
      defaultOpen
      trailing={(
        <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end w-full">
          {hubFilter}
          {asOn ? <span className="text-[11px] sm:text-xs text-gray-400 whitespace-nowrap">As on {asOn}</span> : null}
        </div>
      )}
    >
      <div className="flex gap-3 overflow-x-auto chip-scroll">
        {COLUMNS.map((column) => (
          <div
            key={column.key}
            className="shrink-0 w-full sm:w-[calc((100%-0.75rem)/2)] lg:w-[calc((100%-1.5rem)/3)]"
          >
            <CargoColumn
              column={column}
              data={data?.[column.key]}
              onOpen={onOpenBucket}
            />
          </div>
        ))}
      </div>
    </CollapsiblePanel>
  );
};

export default CargoOverviewSection;
